
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Archive, Filter, CheckCircle2, XCircle, RefreshCcw } from 'lucide-react';
import { supabase } from '@/lib/supabaseClient';
import { ensureAuthReady } from '@/lib/ensureAuthReady';
import { toast } from 'sonner';
import { usePageFocus } from '@/hooks/usePageFocus';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { companyService } from '@/services/companyService';
import { useLanguage } from '@/contexts/LanguageContext';
import { ROUTES } from '@/lib/routes';
import { decodeHtmlEntities } from '@/lib/utils';
import type { CategoryKey } from '@/lib/navigation';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

const PRODUCT_CATEGORY: CategoryKey = 'product';
import { triggerReloadSearch, pickReloadSearch } from '@/utils/reloadSearch';

interface StockItem {
  sku_id: string;
  sku_name: string;
  brand: string;
  category_code: string;
  warehouse: string;
  weight: number;
  weight_unit: string;
  sku_status: string;
  merchant_inventory_qty: number;
  barcode?: string | null;
}

const StockManagement = () => {
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [filteredItems, setFilteredItems] = useState<StockItem[]>([]);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('ONLINE');
  const [barcodeQuery, setBarcodeQuery] = useState<string>('');
  const [categoryMap, setCategoryMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);

  // 跟踪加载开始时间，用于检测stale loading状态
  const loadingStartedAtRef = useRef<number | null>(null);

  // 跟踪stale loading恢复定时器
  const staleRecoveryTimerRef = useRef<number | null>(null);

  const navigate = useNavigate();
  const { language } = useLanguage();
  
  // 首次載入時回放搜尋（恢复UI并自动执行搜索）
  useEffect(() => {
    const pending = pickReloadSearch();
    if (!pending) return;

    (async () => {
      console.log('🔄 库存管理：检测到待回放的搜索，开始执行');
      try {
        // 确保 Auth/Session ready 再搜
        await supabase.auth.getSession();
        
        // 回填 UI
        const filters = pending.filters;
        if (filters.categoryFilter !== undefined) setCategoryFilter(filters.categoryFilter);
        if (filters.brandFilter !== undefined) setBrandFilter(filters.brandFilter);
        if (filters.statusFilter !== undefined) setStatusFilter(filters.statusFilter);
        if (filters.barcodeQuery !== undefined) setBarcodeQuery(filters.barcodeQuery);
        
        // 延迟一点时间让UI状态更新，然后执行搜索
        setTimeout(async () => {
          await fetchStockData();
        }, 500);
        
      } catch (error) {
        console.error('库存管理回放搜索失败:', error);
        toast.error(language === 'zh' ? '搜索恢复失败，请重新搜索' : 'Search restore failed, please search again');
      }
    })();
  }, []); // 只在组件挂载时执行一次

  // 页面焦点恢复处理器
  const handlePageFocusRecovery = useCallback(async () => {
    const now = Date.now();
    const STALE_LOADING_THRESHOLD = 3000; // 3秒

    // 检查是否是stale loading状态
    if (loading && loadingStartedAtRef.current) {
      const loadingDuration = now - loadingStartedAtRef.current;
      if (loadingDuration > STALE_LOADING_THRESHOLD) {
        console.log(`🔄 檢測到stale loading狀態 (${loadingDuration}ms)，重置loading狀態`);
        setLoading(false);
        loadingStartedAtRef.current = null;
      } else {
        // 仍在阈值内，安排定时器在剩余时间内触发恢复
        const remainingTime = STALE_LOADING_THRESHOLD - loadingDuration;
        console.log(`🔄 庫存管理頁面正在加載中 (${loadingDuration}ms)，安排${remainingTime}ms後自動恢復`);

        // 清除任何现有的定时器
        if (staleRecoveryTimerRef.current) {
          clearTimeout(staleRecoveryTimerRef.current);
        }

        // 安排自动恢复
        staleRecoveryTimerRef.current = window.setTimeout(async () => {
          console.log('🔄 自動恢復：重置loading狀態並重新加載數據');
          setLoading(false);
          loadingStartedAtRef.current = null;
          staleRecoveryTimerRef.current = null;

          try {
            await fetchStockData();
          } catch (error) {
            console.error('庫存管理頁面自動恢復失敗:', error);
          }
        }, remainingTime);

        return;
      }
    } else if (loading) {
      console.log('🔄 庫存管理頁面正在加載中，跳過焦點恢復');
      return;
    }

    try {
      console.log('🔄 庫存管理頁面焦點恢復，重新加載數據');
      await fetchStockData();
    } catch (error) {
      console.error('庫存管理頁面焦點恢復失敗:', error);
    }
  }, [loading]);

  // 使用页面焦点恢复 hook
  // 禁用自動頁面焦點恢復，由用戶手動控制
  const { triggerFocusRecovery } = usePageFocus({
    onFocus: handlePageFocusRecovery,
    debounceMs: 1000,
    enabled: false
  });

  // 組件卸載時清理狀態
  useEffect(() => {
    return () => {
      // 清理搜索和過濾相關狀態
      setStockItems([]);
      setFilteredItems([]);
      setCategoryFilter('all');
      setBrandFilter('all');
      setStatusFilter('all');
      setBarcodeQuery('');
      setLoading(false);
    };
  }, []);
  


  useEffect(() => {
    applyFilters();
  }, [stockItems, categoryFilter, brandFilter, statusFilter, barcodeQuery]);


  const fetchStockData = async () => {
    setLoading(true);
    loadingStartedAtRef.current = Date.now(); // 记录加载开始时间
    try {
      // 确保 Auth 就緒
      await ensureAuthReady();

      const currentCompany = await companyService.getCurrentCompany();
      const companyIdNum = currentCompany ? Number(currentCompany.id) : null;

      // 严格限制：仅查询当前公司的产品和库存数据，绝对不使用任何回退查询
      if (!companyIdNum || companyIdNum <= 0) {
        throw new Error('無效的公司ID，無法載入庫存資料');
      }

      // 强制使用公司过滤的数据库查询
      const { data: products, error: productsError } = await supabase
        .from('products')
        .select('sku_id, sku_name, sku_name_chi, brand, category_code, warehouse, weight, weight_unit, sku_status, barcode')
        .eq('company_id', companyIdNum);

      if (productsError) throw productsError;

      const { data: inventory, error: inventoryError } = await supabase
        .from('product_inventory')
        .select('sku_id, merchant_inventory_qty')
        .eq('company_id', companyIdNum);

      if (inventoryError) throw inventoryError;

      // Merge products with inventory data
      const mergedData = products?.map(product => {
        const inventoryItem = inventory?.find(inv => inv.sku_id === product.sku_id);
        return {
          ...product,
          merchant_inventory_qty: inventoryItem?.merchant_inventory_qty || 0
        };
      }) || [];

      // Load category names for used category codes only
      const usedCodes = Array.from(new Set((mergedData || []).map((p) => p.category_code).filter(Boolean)));
      let categoryMap: Record<string, string> = {};
      
      if (usedCodes.length > 0) {
        const { data: cats } = await supabase
          .from('categories')
          .select('category_code, category_name')
          .in('category_code', usedCodes as any);
        (cats || []).forEach((c: any) => { if (c?.category_code) categoryMap[c.category_code] = c.category_name || ''; });
      }

      console.log('庫存數據載入成功:', mergedData.length, '項產品');
      setStockItems(mergedData);
      setCategoryMap(categoryMap);
    } catch (error: any) {
      console.error('获取库存数据失败:', error);
      toast.error(language === 'zh' ? `獲取庫存資料失敗: ${error.message}` : `Failed to fetch stock data: ${error.message}`);
    } finally {
      setLoading(false);
      loadingStartedAtRef.current = null; // 清除加载开始时间

      // 清除stale recovery定时器
      if (staleRecoveryTimerRef.current) {
        clearTimeout(staleRecoveryTimerRef.current);
        staleRecoveryTimerRef.current = null;
      }
    }
  };

  const applyFilters = () => {
    let filtered = [...stockItems];

    if (barcodeQuery.trim()) {
      const q = barcodeQuery.trim().toLowerCase();
      filtered = filtered.filter(item =>
        String(item.sku_id || '').toLowerCase().includes(q) ||
        String(item.barcode || '').toLowerCase().includes(q)
      );
    }

    if (categoryFilter !== 'all') {
      filtered = filtered.filter(item => item.category_code === categoryFilter);
    }
    if (brandFilter !== 'all') {
      filtered = filtered.filter(item => item.brand === brandFilter);
    }
    if (statusFilter !== 'all') {
      filtered = filtered.filter(item => item.sku_status === statusFilter);
    }

    setFilteredItems(filtered);
  };

  const getUniqueValues = (field: keyof StockItem) => {
    const vals = [...new Set(stockItems.map(item => item[field]).filter(Boolean))] as string[];
    if (field === 'brand') {
      return vals.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
    }
    if (field === 'category_code') {
      const num = (s: string) => {
        const n = Number(String(s).replace(/\D/g, ''));
        return Number.isFinite(n) ? n : Number.MAX_SAFE_INTEGER;
      };
      return vals.sort((a, b) => num(a) - num(b));
    }
    return vals;
  };

  const getStatusBadge = (status: string) => {
    const statusColors: { [key: string]: string } = {
      'ACTIVE': 'bg-green-600/20 text-green-400 border-green-600/30',
      'INACTIVE': 'bg-red-600/20 text-red-400 border-red-600/30',
      'PENDING': 'bg-yellow-600/20 text-yellow-400 border-yellow-600/30'
    };
    
    return statusColors[status] || 'bg-gray-600/20 text-gray-400 border-gray-600/30';
  };

  const getStockBadge = (qty: number) => {
    // 低庫存規則：<= 2 警示（紅色）；>12 視為過多（紫色）；其他正常（綠色）
    if (qty <= 2) return 'bg-red-600/20 text-red-400 border-red-600/30';
    if (qty > 12) return 'bg-purple-600/20 text-purple-300 border-purple-600/30';
    return 'bg-green-600/20 text-green-400 border-green-600/30';
  };

  const renderStatusIcon = (status: string) => {
    const isOnline = String(status).toUpperCase() === 'ONLINE';
    const clr = isOnline ? 'text-emerald-400' : 'text-slate-400';
    const label = isOnline ? (language === 'zh' ? '上線' : 'ONLINE') : (language === 'zh' ? '離線' : 'OFFLINE');
    const Icon = isOnline ? CheckCircle2 : XCircle;
    return (
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <span className={`inline-flex items-center ${clr}`}>
              <Icon className="w-5 h-5" />
            </span>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const handleRefreshSearch = () => {
    // 收集当前的筛选条件
    const filters = {
      categoryFilter,
      brandFilter,
      statusFilter,
      barcodeQuery
    };
    
    console.log('🔄 库存管理：触发专业刷新搜索，条件:', filters);
    
    // 触发刷新机制（先保存条件，再刷新页面，页面加载后会自动执行搜索）
    triggerReloadSearch(filters);
  };

  const handleExportCsv = () => {
    const protectText = (v: any) => (v === null || v === undefined ? '' : `'${String(v)}`);
    const headers = [
      'SKU ID',
      language === 'zh' ? '品牌' : 'Brand',
      language === 'zh' ? '產品名稱' : 'Product Name',
      language === 'zh' ? '分類代碼' : 'Category Code',
      language === 'zh' ? '分類名稱' : 'Category Name',
      language === 'zh' ? '存量' : 'Quantity',
      language === 'zh' ? '狀態' : 'Status',
    ];
    const rows = filteredItems.map((it) => [
      protectText(it.sku_id),
      it.brand || '',
      language === 'zh' ? ((it as any).sku_name_chi || it.sku_name || '') : (it.sku_name || ''),
      protectText(it.category_code || ''),
      categoryMap[String(it.category_code)] || '',
      String(it.merchant_inventory_qty ?? ''),
      String(it.sku_status || ''),
    ]);
    const csv = [headers, ...rows]
      .map((r) => r.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory_${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // KPI：依照目前搜尋/篩選結果即時更新
  const totalItems = filteredItems.length;
  const totalQuantity = filteredItems.reduce((sum, item) => sum + item.merchant_inventory_qty, 0);
  const lowStockItems = filteredItems.filter(item => item.merchant_inventory_qty <= 2).length;
  const overStockItems = filteredItems.filter(item => item.merchant_inventory_qty > 12).length;

  return (
    <div className="min-h-screen bg-background p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="outline"
            onClick={() => {
              localStorage.setItem('selectedCategory', PRODUCT_CATEGORY);
              navigate(ROUTES.dashboard.root);
            }}
            className="bg-card/50 border-border text-muted-foreground hover:bg-secondary/50"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {language === 'zh' ? '返回主控台' : 'Back to Dashboard'}
          </Button>
          <div className="flex items-center gap-2">
            <Archive className="w-6 h-6 text-purple-400" />
            <h1 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{language === 'zh' ? '庫存管理' : 'Inventory Management'}</h1>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <Card className="bg-card/80 border-border">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-foreground">{totalItems}</p>
                <p className="text-muted-foreground text-sm">{language === 'zh' ? '產品項目' : 'Product Item'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-400">{totalQuantity}</p>
                <p className="text-muted-foreground text-sm">{language === 'zh' ? '總庫存量' : 'Total Inventory'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-yellow-400">{lowStockItems}</p>
                <p className="text-muted-foreground text-sm">{language === 'zh' ? '低庫存警告 (≤2)' : 'Low Stock Alerts (≤2)'}</p>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-card/80 border-border">
            <CardContent className="p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-purple-400">{overStockItems}</p>
                <p className="text-muted-foreground text-sm">{language === 'zh' ? '過多存貨提醒 (>12)' : 'Overstock Alerts (>12)'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="bg-card/80 border-border mb-6">
          <CardHeader>
            <CardTitle className="text-foreground flex items-center gap-2">
              <Filter className="w-5 h-5" />
              {language === 'zh' ? '篩選條件' : 'Filters'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm text-muted-foreground mb-2">{language === 'zh' ? '類別' : 'Category'}</label>
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '選擇類別' : 'Select category'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部類別' : 'All Categories'}</SelectItem>
                    {getUniqueValues('category_code').map(code => (
                      <SelectItem key={code as string} value={code as string} className="focus:bg-accent data-[highlighted]:bg-accent">
                        <div className="flex flex-col">
                          <span className="text-sm font-mono">{String(code)}</span>
                          {categoryMap[String(code)] && (
                            <span className="text-xs text-muted-foreground">{categoryMap[String(code)]}</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-muted-foreground mb-2">{language === 'zh' ? '品牌' : 'Brand'}</label>
                <Select value={brandFilter} onValueChange={setBrandFilter}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '選擇品牌' : 'Select brand'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部品牌' : 'All Brands'}</SelectItem>
                    {getUniqueValues('brand').map(brand => (
                      <SelectItem key={brand} value={brand as string} className="focus:bg-accent data-[highlighted]:bg-accent">{brand}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-muted-foreground mb-2">{language === 'zh' ? '狀態' : 'Status'}</label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="bg-background/50 border-input text-foreground">
                    <SelectValue placeholder={language === 'zh' ? '選擇狀態' : 'Select status'} />
                  </SelectTrigger>
                  <SelectContent className="bg-popover border-border text-popover-foreground">
                    <SelectItem value="all">{language === 'zh' ? '全部狀態' : 'All Statuses'}</SelectItem>
                    <SelectItem value="ONLINE" className="focus:bg-accent data-[highlighted]:bg-accent">ONLINE</SelectItem>
                    <SelectItem value="OFFLINE" className="focus:bg-accent data-[highlighted]:bg-accent">OFFLINE</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm text-muted-foreground mb-2">{language === 'zh' ? '條碼/SKU 搜尋' : 'Barcode/SKU search'}</label>
                <Input
                  value={barcodeQuery}
                  onChange={(e) => setBarcodeQuery(e.target.value)}
                  placeholder={language === 'zh' ? '輸入條碼或 SKU 進行搜尋' : 'Enter barcode or SKU to search'}
                  className="bg-background/50 border-input text-foreground"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <Button onClick={handleRefreshSearch} className="bg-blue-600 hover:bg-blue-700 text-white">
                <RefreshCcw className="w-4 h-4 mr-2" />
                {language === 'zh' ? '刷新搜索' : 'Refresh Search'}
              </Button>
              <Button onClick={handleExportCsv} className="bg-purple-600 hover:bg-purple-700 text-white">
                {language === 'zh' ? '匯出 CSV' : 'Export CSV'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Stock Table */}
        <Card className="bg-card/80 border-border">
          <CardHeader>
            <CardTitle className="text-foreground">{language === 'zh' ? '庫存資料' : 'Inventory Information'}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400 mx-auto"></div>
                <p className="text-muted-foreground mt-2">載入中...</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-border">
                      <TableHead className="text-muted-foreground">SKU ID</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '品牌' : 'Brand'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '產品名稱' : 'Product Name'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '類別' : 'Category'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '存量' : 'Quantity'}</TableHead>
                      <TableHead className="text-muted-foreground">{language === 'zh' ? '狀態' : 'Status'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredItems.map((item, index) => (
                      <TableRow key={index} className="border-border hover:bg-accent/50">
                        <TableCell className="text-foreground font-mono">{item.sku_id}</TableCell>
                        <TableCell className="text-muted-foreground">{item.brand}</TableCell>
                        <TableCell className="text-foreground">
                          {decodeHtmlEntities(language === 'zh' ? ((item as any).sku_name_chi || item.sku_name || '') : (item.sku_name || ''))}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div className="flex flex-col">
                            <span className="text-sm font-mono">{item.category_code}</span>
                            {categoryMap[String(item.category_code)] && (
                              <span className="text-xs text-muted-foreground">{categoryMap[String(item.category_code)]}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={getStockBadge(item.merchant_inventory_qty)}>
                            {item.merchant_inventory_qty}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {renderStatusIcon(item.sku_status)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                
                {filteredItems.length === 0 && (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">沒有找到符合條件的庫存資料</p>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default StockManagement;
